import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Card } from '@components/ui/Card'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { motion } from 'framer-motion'
import { CheckCircle, BookOpen, Gift, Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'

export default function TrainingSuccess() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { signIn } = useAuth()

  const sessionId = searchParams.get('session_id')

  // Data state
  const [purchaseData, setPurchaseData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Account setup state
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [settingPassword, setSettingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [accountSetupComplete, setAccountSetupComplete] = useState(false)

  // Fetch purchase data
  const fetchPurchaseData = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided')
      setLoading(false)
      return
    }

    try {
      const response = await fetch(`/api/training/purchase-success/${sessionId}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch purchase details')
      }

      setPurchaseData(data)

      // If purchase is still pending, poll for updates
      if (data.purchasePending) {
        setTimeout(() => fetchPurchaseData(), 2000)
      }
    } catch (err) {
      console.error('Error fetching purchase data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    fetchPurchaseData()
  }, [fetchPurchaseData])

  const handleSetPassword = async (e) => {
    e.preventDefault()
    setPasswordError(null)

    // Validate passwords
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      return
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setSettingPassword(true)

    try {
      const response = await fetch('/api/training/complete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: purchaseData.user.userId,
          sessionId,
          password
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set password')
      }

      // Sign in with new password (skip default redirect)
      const { error: signInError } = await signIn(
        purchaseData.user.email,
        password,
        { skipRedirect: true }
      )

      if (signInError) {
        console.error('Auto sign-in failed:', signInError)
        // Still mark as complete, user can sign in manually
        setAccountSetupComplete(true)
      } else {
        // Navigate directly to the training course
        navigate('/training/photography-fundamentals')
      }
    } catch (err) {
      console.error('Error setting password:', err)
      setPasswordError(err.message)
    } finally {
      setSettingPassword(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-[#fe395f] animate-spin mx-auto mb-3 sm:mb-4" />
          <p className="text-sm sm:text-base text-[#606b7c]">Loading your purchase details...</p>
        </motion.div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-5 sm:p-8 text-center">
            <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4" />
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-sm sm:text-base text-[#606b7c] mb-4 sm:mb-6">{error}</p>
            <Button
              onClick={() => navigate('/training/photography-fundamentals/purchase')}
              className="bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
            >
              Return to Purchase Page
            </Button>
          </Card>
        </motion.div>
      </div>
    )
  }

  // Purchase pending (webhook still processing)
  if (purchaseData?.purchasePending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-[#fe395f] animate-spin mx-auto mb-3 sm:mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Processing your purchase...
          </h2>
          <p className="text-sm sm:text-base text-[#606b7c]">This should only take a moment.</p>
        </motion.div>
      </div>
    )
  }

  const isGift = purchaseData?.isGift
  const needsPasswordSetup = purchaseData?.user?.userId && !purchaseData?.user?.hasAuth && !accountSetupComplete
  const recipientEmail = purchaseData?.user?.email

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="p-5 sm:p-8">
          {/* Success Header */}
          <div className="text-center mb-4 sm:mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full mb-3 sm:mb-4"
            >
              {isGift ? (
                <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              ) : (
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              )}
            </motion.div>

            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {isGift ? 'Gift Purchase Complete!' : 'Purchase Successful!'}
            </h1>

            <p className="text-sm sm:text-base text-[#606b7c]">
              {isGift ? (
                <>
                  Your gift has been sent to <strong>{recipientEmail}</strong>. They&apos;ll receive an email with instructions to access the course.
                </>
              ) : (
                <>
                  Thank you for your purchase! You now have lifetime access to Photography Fundamentals.
                </>
              )}
            </p>
          </div>

          {/* Account Setup (for non-gift express checkout) */}
          {needsPasswordSetup && !isGift && (
            <div className="border-t border-gray-200 pt-4 sm:pt-6 mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Set Up Your Account
              </h2>
              <p className="text-xs sm:text-sm text-[#606b7c] mb-3 sm:mb-4">
                Create a password to access your course and save your progress.
              </p>

              <form onSubmit={handleSetPassword} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={recipientEmail}
                    disabled
                    className="bg-gray-50 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    minLength={6}
                    className="text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="text-sm"
                  />
                </div>

                {passwordError && (
                  <div className="p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm">
                    {passwordError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={settingPassword}
                  className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
                >
                  {settingPassword ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Setting up account...
                    </div>
                  ) : (
                    'Create Account & Continue'
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Account setup complete message */}
          {accountSetupComplete && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">Account created successfully!</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2 sm:space-y-3">
            {/* Show Start Learning only if NOT a gift AND (no password setup needed OR account setup complete) */}
            {!isGift && (!needsPasswordSetup || accountSetupComplete) && (
              <Button
                onClick={() => navigate('/training/photography-fundamentals')}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Start Learning
              </Button>
            )}

            {isGift && (
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
              >
                Return Home
              </Button>
            )}

            {purchaseData?.purchase && (
              <p className="text-xs text-center text-[#606b7c]">
                Purchase ID: {purchaseData.purchase.id}
              </p>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  )
}
